import React, { useState, useEffect } from 'react';
import { productService } from '../../services/ProductService';
import { Product } from '../../types';
import { Package, Search, Edit2, Eye, EyeOff, Image as ImageIcon, CheckCircle2, DollarSign, Filter, X } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { EditProductModal } from '../components/EditProductModal';

interface ProductsPageProps {
    isCreating?: boolean;
    onSetIsCreating?: (val: boolean) => void;
    userRole?: string;
}

const ProductsPage: React.FC<ProductsPageProps> = ({ isCreating, onSetIsCreating, userRole }) => {
    // Data State
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // UI State
    const [page, setPage] = useState(1);
    const itemsPerPage = 20;

    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [uiFilters, setUiFilters] = useState<{ category: string[], make: string[], model: string[] }>({
        category: [],
        make: [],
        model: []
    });

    // Applied Filters (Trigger for API)
    const [activeFilters, setActiveFilters] = useState<{ category: string[], make: string[], model: string[] }>({
        category: [],
        make: [],
        model: []
    });

    const [editingProduct, setEditingProduct] = useState<Product | null>(null);

    const [availableFilters, setAvailableFilters] = useState<{ categories: string[], makes: string[], models: string[] }>({ categories: [], makes: [], models: [] });

    // Unified Data Loader (Coordinated Effect)
    useEffect(() => {
        const timer = setTimeout(() => {
            loadProducts(page, searchQuery, activeFilters);
        }, searchQuery ? 500 : 0); // Debounce only for search

        return () => clearTimeout(timer);
    }, [page, searchQuery, activeFilters]);

    // Reset page on filter/search change
    useEffect(() => {
        if (page !== 1) setPage(1);
    }, [searchQuery, activeFilters]);

    // Initial load for filters
    useEffect(() => {
        let isMounted = true;
        loadFilters();

        const unsubscribe = productService.subscribeToProducts((updatedProduct, eventType) => {
            if (!isMounted) return;
            if (eventType === 'UPDATE') {
                setProducts(prev => prev.map(p => {
                    if (p.id === updatedProduct.id) {
                        return {
                            ...p,
                            ...updatedProduct,
                            is_magsafe: updatedProduct.is_magsafe ?? p.is_magsafe,
                            is_premium: updatedProduct.is_premium ?? p.is_premium,
                            is_last_units: updatedProduct.is_last_units ?? p.is_last_units,
                            is_active: updatedProduct.is_active ?? p.is_active,
                            variants: p.variants, // PRESERVE variants explicitly!
                        };
                    }
                    return p;
                }));
            } else if (eventType === 'DELETE') {
                setProducts(prev => prev.filter(p => p.id !== updatedProduct.id));
                if (editingProduct && editingProduct.id === updatedProduct.id) setEditingProduct(null);
            } else if (eventType === 'INSERT') {
                loadProducts(page, searchQuery, activeFilters);
            }
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    const loadFilters = async () => {
        const filters = await productService.getUniqueFilters();
        setAvailableFilters(filters);
    };

    const loadProducts = async (currentPage: number, query: string, filtersState: typeof activeFilters) => {
        setIsLoading(true);
        try {
            const { data, total } = await productService.getPaginatedProducts(currentPage, itemsPerPage, query, filtersState);
            setProducts(data);
            setTotalItems(total);
            setTotalPages(Math.ceil(total / itemsPerPage));
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplyFilters = () => {
        setActiveFilters(uiFilters);
    };

    const handleCommitSearch = () => {
        setSearchQuery(searchInput);
        setPage(1);
    };

    const handleToggleVisibility = async (product: Product) => {
        const newStatus = !(product.is_active ?? true);
        setProducts(current => current.map(p =>
            p.id === product.id ? { ...p, is_active: newStatus } : p
        ));
        try {
            await productService.toggleProductVisibility(product.id, !newStatus);
        } catch (error) {
            console.error('Error toggling visibility:', error);
            setProducts(current => current.map(p =>
                p.id === product.id ? { ...p, is_active: !newStatus } : p
            ));
        }
    };

    const handleEdit = (id: string) => {
        const product = products.find(p => p.id === id);
        if (product) {
            setEditingProduct(product);
        }
    };

    const handleSaveProduct = async (id: string, updates: Partial<Product>) => {
        try {
            const updatedProduct = await productService.updateProduct(id, updates);
            setProducts(current => current.map(p => p.id === id ? updatedProduct : p));
            setEditingProduct(null);
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error al guardar el producto. Intente nuevamente.');
        }
    };

    const handleCreateProduct = async (product: Partial<Product>) => {
        try {
            await productService.createProduct(product as any);
            // Refrescar inmediatamente para feedback visual rápido
            loadProducts(1, searchQuery, activeFilters);
        } catch (error) {
            console.error('Error creating product:', error);
            alert('Error al crear el producto.');
            throw error;
        }
    };

    const handleDeleteProduct = async (id: string) => {
        try {
            await productService.deleteProduct(id);
            if (editingProduct?.id === id) setEditingProduct(null);
        } catch (error) {
            console.error('Error deleting product:', error);
            alert('Error al eliminar el producto.');
        }
    };

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

    return (
        <div className="space-y-6">
            {/* Cabecera de Página (Desktop) / Barra Sticky (Mobile) */}
            <div className="sticky top-[64px] md:static bg-black/80 md:bg-transparent backdrop-blur-md md:backdrop-blur-none z-30 -mx-4 md:mx-0 px-4 md:px-0 py-3 md:p-0 border-b border-zinc-900 md:border-none">
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                    <h2 className="hidden md:block text-2xl font-display font-bold uppercase italic shrink-0">Gestión de Catálogo</h2>

                    <div className="flex flex-col md:flex-row gap-2 w-full xl:w-auto items-stretch">
                        {/* 1. SECCIÓN BÚSQUEDA (Input + Botón Buscar siempre juntos) */}
                        <div className="flex gap-2 flex-grow lg:flex-grow-0">
                            <div className="relative flex-grow lg:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 h-4 w-4" />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    className="bg-zinc-900 border border-zinc-800 focus:border-white focus:bg-black outline-none py-3 md:py-2 pl-10 pr-4 text-sm w-full transition-all h-[42px] rounded-sm"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCommitSearch()}
                                />
                            </div>
                            <button
                                onClick={handleCommitSearch}
                                className="bg-white text-black font-bold uppercase text-[10px] px-6 py-2 rounded-sm transition-all flex items-center gap-2 border border-white active:scale-95 shadow-[0_0_10px_rgba(255,255,255,0.2)] h-[42px]"
                            >
                                <Search size={14} />
                                <span className="hidden sm:inline">BUSCAR</span>
                            </button>
                        </div>

                        {/* 2. SECCIÓN FILTROS (Botón móvil o Selectores desktop) */}
                        <div className="flex gap-2 items-stretch flex-grow lg:flex-grow-0">
                            {/* Trigger Filtros Mobile/Tablet */}
                            <button
                                onClick={() => setIsFilterDrawerOpen(true)}
                                className={`lg:hidden flex items-center justify-center gap-2 px-4 flex-grow rounded-sm border transition-all active:scale-95 h-[42px] min-w-[110px] whitespace-nowrap ${(activeFilters.category.length || activeFilters.make.length || activeFilters.model.length) > 0
                                    ? 'bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.4)] font-black'
                                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-500 hover:bg-zinc-800'
                                    }`}
                            >
                                <Filter size={14} className={activeFilters.category.length || activeFilters.make.length || activeFilters.model.length ? 'text-black' : 'text-zinc-500'} />
                                <span className="text-[10px] font-bold uppercase tracking-widest leading-none">Filtros</span>
                                {(activeFilters.category.length || activeFilters.make.length || activeFilters.model.length) > 0 && (
                                    <span className="flex h-2 w-2 relative">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
                                    </span>
                                )}
                            </button>

                            {/* Filtros Desktop */}
                            <div className="hidden lg:grid grid-cols-3 gap-2 flex-grow-[2] min-w-[400px]">
                                <SearchableSelect
                                    value={uiFilters.make}
                                    onChange={(val) => setUiFilters({ ...uiFilters, make: val as string[] })}
                                    options={availableFilters.makes}
                                    placeholder="MARCA"
                                    className="w-full text-xs"
                                    multiple={true}
                                />
                                <SearchableSelect
                                    value={uiFilters.model}
                                    onChange={(val) => setUiFilters({ ...uiFilters, model: val as string[] })}
                                    options={availableFilters.models}
                                    placeholder="MODELO"
                                    className="w-full text-xs"
                                    multiple={true}
                                />
                                <SearchableSelect
                                    value={uiFilters.category}
                                    onChange={(val) => setUiFilters({ ...uiFilters, category: val as string[] })}
                                    options={availableFilters.categories}
                                    placeholder="CATEGORÍA"
                                    className="w-full text-xs"
                                    multiple={true}
                                />
                            </div>

                            <button
                                onClick={handleApplyFilters}
                                className="hidden lg:block bg-zinc-800/50 text-zinc-300 font-bold uppercase text-[10px] px-6 py-2 rounded-sm hover:bg-white hover:text-black transition-colors whitespace-nowrap border border-zinc-700 h-[42px]"
                            >
                                Aplicar Filtros
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de Filtros (Mobile/Tablet Centrado) */}
            {isFilterDrawerOpen && (
                <div className="fixed inset-0 z-[100] lg:hidden flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsFilterDrawerOpen(false)} />

                    <div className="relative w-full max-w-sm bg-zinc-950 border border-zinc-800 rounded-sm p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-display font-bold italic uppercase text-white">Filtrar Catálogo</h3>
                            <button
                                onClick={() => setIsFilterDrawerOpen(false)}
                                className="text-zinc-500 hover:text-white"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-6 mb-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Marcas</label>
                                <SearchableSelect
                                    value={uiFilters.make}
                                    onChange={(val) => setUiFilters({ ...uiFilters, make: val as string[] })}
                                    options={availableFilters.makes}
                                    placeholder="TODAS LAS MARCAS"
                                    multiple={true}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Modelos</label>
                                <SearchableSelect
                                    value={uiFilters.model}
                                    onChange={(val) => setUiFilters({ ...uiFilters, model: val as string[] })}
                                    options={availableFilters.models}
                                    placeholder="TODOS LOS MODELOS"
                                    multiple={true}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Categorías</label>
                                <SearchableSelect
                                    value={uiFilters.category}
                                    onChange={(val) => setUiFilters({ ...uiFilters, category: val as string[] })}
                                    options={availableFilters.categories}
                                    placeholder="TODAS LAS CATEGORÍAS"
                                    multiple={true}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => {
                                    setUiFilters({ category: [], make: [], model: [] });
                                    setActiveFilters({ category: [], make: [], model: [] });
                                    setIsFilterDrawerOpen(false);
                                }}
                                className="py-3 bg-zinc-900 text-zinc-500 font-bold uppercase text-xs rounded-sm border border-zinc-800 hover:bg-zinc-800 transition-colors"
                            >
                                Limpiar
                            </button>
                            <button
                                onClick={() => {
                                    handleApplyFilters();
                                    setIsFilterDrawerOpen(false);
                                }}
                                className="py-3 bg-white text-black font-bold uppercase text-xs rounded-sm shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:bg-zinc-200 transition-colors"
                            >
                                Aplicar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-transparent md:bg-zinc-900/30 md:border md:border-zinc-800 rounded-sm overflow-hidden flex flex-col">
                {/* Desktop View: Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-900 border-b border-zinc-800 text-zinc-500 font-mono uppercase text-[10px] tracking-widest">
                            <tr>
                                <th className="px-6 py-4 w-full">Producto</th>
                                <th className="px-6 py-4">Categoría</th>
                                <th className="px-6 py-4">Variantes</th>
                                <th className="px-6 py-4 text-center">Estado UI</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            <span className="text-zinc-500 italic text-xs">Cargando inventario...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : products.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-10 text-center text-zinc-500 italic">
                                        No se encontraron productos.
                                    </td>
                                </tr>
                            ) : (
                                products.map(product => (
                                    <tr key={product.id} className={`transition-colors group ${product.is_active === false ? 'bg-zinc-900/50' : 'hover:bg-zinc-800/50'}`}>
                                        <td className={`px-6 py-4 ${product.is_active === false ? 'opacity-40 grayscale' : ''}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden shrink-0">
                                                    {product.image ? (
                                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <ImageIcon className="text-zinc-600 h-5 w-5" />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-white leading-tight line-clamp-1">{product.name}</p>
                                                    <p className="text-[10px] font-mono text-zinc-500 uppercase">{product.id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-1 bg-zinc-800 text-zinc-400 text-[10px] uppercase font-bold tracking-tighter whitespace-nowrap">
                                                {product.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {(() => {
                                                const isInStock = product.stock?.online;
                                                return product.variants && product.variants.length > 0 ? (
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-xs text-white font-bold">{product.variants.length} Opciones</span>
                                                        <div className="flex items-center gap-1.5">
                                                            <div className={`w-1.5 h-1.5 rounded-full ${isInStock ? 'bg-white' : 'bg-red-500'}`} />
                                                            <span className={`text-[10px] uppercase font-bold ${isInStock ? 'text-zinc-500' : 'text-red-500'}`}>
                                                                {isInStock ? 'Disponible' : 'Agotado'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${isInStock ? 'bg-white shadow-[0_0_8px_rgba(255,255,255,0.6)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]'}`} />
                                                        <span className={`text-xs uppercase font-bold whitespace-nowrap ${isInStock ? 'text-zinc-400' : 'text-red-500'}`}>
                                                            {isInStock ? 'En Stock' : 'Agotado'}
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                {product.is_premium && (
                                                    <div className="group/tip relative text-white">
                                                        <DollarSign size={14} className="stroke-white" />
                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-black border border-zinc-800 text-[8px] py-1 px-2 invisible group-hover/tip:visible whitespace-nowrap z-10 text-white">EN OFERTA</span>
                                                    </div>
                                                )}
                                                {!product.stock?.online && (
                                                    <div className="group/tip relative">
                                                        <CheckCircle2 size={14} className="text-red-500" />
                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-black border border-zinc-800 text-[8px] py-1 px-2 invisible group-hover/tip:visible whitespace-nowrap z-10">AGOTADO</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(product.id)}
                                                    className="p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all rounded-sm"
                                                    title="Editar Metadatos"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleVisibility(product)}
                                                    className={`p-2 transition-all rounded-sm ${product.is_active === false ? 'text-zinc-600 hover:text-zinc-400' : 'text-white hover:bg-zinc-800'}`}
                                                    title={product.is_active === false ? "Mostrar producto" : "Ocultar producto"}
                                                >
                                                    {product.is_active === false ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View: Cards */}
                <div className="md:hidden space-y-4">
                    {isLoading ? (
                        <div className="py-20 text-center bg-zinc-900/30 border border-zinc-800 rounded-sm">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span className="text-zinc-500 italic text-xs uppercase tracking-widest font-mono">Actualizando...</span>
                            </div>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="py-10 text-center text-zinc-500 italic bg-zinc-900/30 border border-zinc-800 rounded-sm">
                            No hay productos que coincidan.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {products.map(product => (
                                <div
                                    key={product.id}
                                    className={`bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 flex flex-col gap-4 transition-all relative overflow-hidden ${product.is_active === false ? 'grayscale opacity-60' : ''}`}
                                >
                                    {/* Badges Flotantes */}
                                    <div className="absolute top-2 right-2 flex gap-1">
                                        {product.is_premium && <DollarSign size={12} className="text-white" />}
                                        {!product.stock?.online && <CheckCircle2 size={12} className="text-red-500" />}
                                    </div>

                                    {/* Info Principal */}
                                    <div className="flex gap-4">
                                        <div className="w-16 h-16 bg-zinc-800 border border-zinc-700 rounded-sm flex items-center justify-center overflow-hidden shrink-0">
                                            {product.image ? (
                                                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <ImageIcon className="text-zinc-600 h-6 w-6" />
                                            )}
                                        </div>
                                        <div className="flex-grow min-w-0">
                                            <h3 className="text-white font-bold leading-tight break-words">{product.name}</h3>
                                            <p className="text-[9px] font-mono text-zinc-500 mt-1 uppercase truncate">{product.id}</p>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-[9px] uppercase font-bold tracking-tight">
                                                    {product.category}
                                                </span>
                                                <div className="flex items-center gap-1.5 ml-auto">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${product.stock?.online ? 'bg-white shadow-[0_0_5px_rgba(255,255,255,0.5)]' : 'bg-red-500'}`} />
                                                    <span className={`text-[10px] uppercase font-bold ${product.stock?.online ? 'text-zinc-500' : 'text-red-500'}`}>
                                                        {product.stock?.online ? 'Stock' : 'Agotado'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Acciones Mobile */}
                                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-800/50">
                                        <button
                                            onClick={() => handleEdit(product.id)}
                                            className="flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm text-xs font-bold uppercase transition-colors"
                                        >
                                            <Edit2 size={14} />
                                            Editar
                                        </button>
                                        <button
                                            onClick={() => handleToggleVisibility(product)}
                                            className={`flex items-center justify-center gap-2 py-3 rounded-sm text-xs font-bold uppercase transition-colors ${product.is_active === false
                                                ? 'bg-zinc-800 text-zinc-400'
                                                : 'bg-white/10 text-white border border-white/20'
                                                }`}
                                        >
                                            {product.is_active === false ? (
                                                <>
                                                    <EyeOff size={14} />
                                                    Mostrar
                                                </>
                                            ) : (
                                                <>
                                                    <Eye size={14} />
                                                    Ocultar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                <div className="mt-4 md:mt-0 border-t border-zinc-800 p-4 flex flex-col sm:flex-row items-center justify-between bg-zinc-900/50 gap-4">
                    <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest order-2 sm:order-1">
                        Mostrando <span className="text-white font-bold">{products.length}</span> de <span className="text-white font-bold">{totalItems}</span> productos
                    </p>

                    <div className="flex items-center justify-between w-full sm:w-auto gap-4 order-1 sm:order-2">
                        <button
                            onClick={() => {
                                setPage(p => Math.max(1, p - 1));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={page === 1 || isLoading}
                            className="flex-1 sm:flex-none px-6 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs hover:text-white hover:border-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all uppercase font-bold tracking-widest active:scale-95"
                        >
                            Ant.
                        </button>
                        <span className="px-2 text-[10px] font-mono text-zinc-500 uppercase">
                            Pag <span className="text-white font-bold">{page}</span> / {totalPages || 1}
                        </span>
                        <button
                            onClick={() => {
                                setPage(p => Math.min(totalPages, p + 1));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={page === totalPages || isLoading}
                            className="flex-1 sm:flex-none px-6 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs hover:text-white hover:border-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all uppercase font-bold tracking-widest active:scale-95"
                        >
                            Sig.
                        </button>
                    </div>
                </div>
            </div>

            {
                (editingProduct || isCreating) && (
                    <EditProductModal
                        product={editingProduct}
                        isOpen={!!editingProduct || !!isCreating}
                        onClose={() => {
                            setEditingProduct(null);
                            if (onSetIsCreating) onSetIsCreating(false);
                        }}
                        onSave={handleSaveProduct}
                        onCreate={handleCreateProduct}
                        onDelete={handleDeleteProduct}
                        availableOptions={availableFilters}
                        userRole={userRole}
                    />
                )
            }
        </div >
    );
};

export default ProductsPage;
